import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setCuitProfileHeader } from '@/api/client';
import { useAuth } from './useAuth';

/**
 * Selección de CUIT activa para el portal — pieza clave del UX del contador con N CUITs.
 *
 * - `currentCuitProfileId === null` significa "Todos los CUITs" (default).
 * - Cualquier otro valor es un GUID del CuitProfile activo en el tenant.
 *
 * Persistimos en localStorage por tenant (`afip-current-cuit-{tenantId}`) — un mismo navegador
 * usado por dos cuentas distintas no se pisa la selección. Cuando el AuthContext cambia el
 * tenant (login / logout), reseteamos a null.
 *
 * Cuando hay un CUIT activo, el cliente axios envía el header `X-Cuit-Profile-Id`. El backend
 * de V1 NO valida ni filtra por este header — es un client-side hint para que componentes que
 * listen (Invoices, Audit) filtren localmente, y queda preparado para que en V2 el server
 * empuje el filtro.
 */
export interface CuitContextValue {
  currentCuitProfileId: string | null;
  setCurrentCuitProfile: (id: string | null) => void;
}

export const CuitContext = createContext<CuitContextValue | null>(null);

const STORAGE_PREFIX = 'afip-current-cuit-';

function storageKey(tenantId: string): string {
  return `${STORAGE_PREFIX}${tenantId}`;
}

function readPersisted(tenantId: string | null): string | null {
  if (!tenantId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(tenantId));
    return raw && raw !== 'all' ? raw : null;
  } catch {
    return null;
  }
}

interface CuitProviderProps {
  children: ReactNode;
}

export function CuitProvider({ children }: CuitProviderProps): JSX.Element {
  const { tenantId } = useAuth();
  const [currentCuitProfileId, setCurrentInner] = useState<string | null>(() =>
    readPersisted(tenantId),
  );

  // Si cambia el tenant (login/logout o switch), releo el valor persistido o reseteo a null.
  useEffect(() => {
    setCurrentInner(readPersisted(tenantId));
  }, [tenantId]);

  // Sync con el axios client — header opcional.
  useEffect(() => {
    setCuitProfileHeader(currentCuitProfileId);
    return () => {
      setCuitProfileHeader(null);
    };
  }, [currentCuitProfileId]);

  const setCurrentCuitProfile = useCallback(
    (id: string | null): void => {
      setCurrentInner(id);
      if (tenantId && typeof window !== 'undefined') {
        try {
          if (id) {
            window.localStorage.setItem(storageKey(tenantId), id);
          } else {
            window.localStorage.removeItem(storageKey(tenantId));
          }
        } catch {
          // localStorage puede fallar en private browsing — ignoramos.
        }
      }
    },
    [tenantId],
  );

  const value = useMemo<CuitContextValue>(
    () => ({ currentCuitProfileId, setCurrentCuitProfile }),
    [currentCuitProfileId, setCurrentCuitProfile],
  );

  return <CuitContext.Provider value={value}>{children}</CuitContext.Provider>;
}

export function useCurrentCuit(): CuitContextValue {
  const ctx = useContext(CuitContext);
  if (!ctx) {
    throw new Error('useCurrentCuit must be used within a CuitProvider.');
  }
  return ctx;
}
